import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { db } from "@/lib/billing";
import type { ServiceRecord } from "@/lib/packageAccess";

const emptyService: Partial<ServiceRecord> = {
  name: "",
  slug: "",
  description: "",
  is_active: true,
  is_admin_only: false,
  is_user_visible: true,
  required_integration: "",
  feature_flags: {},
  limits: {},
  included_package_slugs: [],
  sort_order: 100,
};

async function fetchServices(): Promise<ServiceRecord[]> {
  const { data, error } = await db.from("service_definitions").select("*").order("sort_order");
  if (error) throw error;
  return data ?? [];
}

export default function AdminServicesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<ServiceRecord> | null>(null);
  const { data: services = [], isLoading } = useQuery({ queryKey: ["admin-services"], queryFn: fetchServices });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return services.filter((service) => [service.name, service.slug, service.description ?? "", service.required_integration ?? ""].join(" ").toLowerCase().includes(q));
  }, [search, services]);

  const save = useMutation({
    mutationFn: async (service: Partial<ServiceRecord>) => {
      const payload = {
        ...service,
        slug: service.slug || service.name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      };
      const { error } = service.id
        ? await db.from("service_definitions").update(payload).eq("id", service.id)
        : await db.from("service_definitions").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-services"] });
      setEditing(null);
      toast.success("Service enregistre");
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible d'enregistrer le service"),
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold">Services</h1>
          <p className="text-xs text-muted-foreground">Catalogue des services, limites, integrations et packages inclus.</p>
        </div>
        <Button size="sm" onClick={() => setEditing(emptyService)}><Plus className="mr-2 h-4 w-4" />Nouveau service</Button>
      </header>
      <main className="grid gap-6 p-6 xl:grid-cols-[1fr_420px]">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Definitions de services</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Packages</TableHead><TableHead>Integration</TableHead><TableHead>Visibilite</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filtered.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell>
                        <div className="font-medium">{service.name}</div>
                        <div className="text-xs text-muted-foreground">{service.slug}</div>
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <div className="flex flex-wrap gap-1">{service.included_package_slugs.map((slug) => <Badge key={slug} variant="outline">{slug}</Badge>)}</div>
                      </TableCell>
                      <TableCell>{service.required_integration ?? "Aucune"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {service.is_active && <Badge><Check className="mr-1 h-3 w-3" />Actif</Badge>}
                          {service.is_admin_only && <Badge variant="secondary">Admin</Badge>}
                          {service.is_user_visible && <Badge variant="outline">Utilisateur</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setEditing(service)}><Settings2 className="mr-2 h-4 w-4" />Editer</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <ServiceEditor value={editing} onChange={setEditing} onSave={(service) => save.mutate(service)} saving={save.isPending} />
      </main>
    </div>
  );
}

function ServiceEditor({ value, onChange, onSave, saving }: { value: Partial<ServiceRecord> | null; onChange: (value: Partial<ServiceRecord> | null) => void; onSave: (value: Partial<ServiceRecord>) => void; saving: boolean }) {
  if (!value) return <Card><CardContent className="p-6 text-sm text-muted-foreground">Selectionnez un service ou creez-en un nouveau.</CardContent></Card>;
  const set = (patch: Partial<ServiceRecord>) => onChange({ ...value, ...patch });
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{value.id ? "Editer le service" : "Nouveau service"}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Nom"><Input value={value.name ?? ""} onChange={(e) => set({ name: e.target.value })} /></Field>
          <Field label="Slug"><Input value={value.slug ?? ""} onChange={(e) => set({ slug: e.target.value })} /></Field>
        </div>
        <Field label="Description"><Textarea value={value.description ?? ""} onChange={(e) => set({ description: e.target.value })} /></Field>
        <Field label="Packages inclus (slugs separes par virgule)">
          <Input value={(value.included_package_slugs ?? []).join(", ")} onChange={(e) => set({ included_package_slugs: e.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} />
        </Field>
        <Field label="Integration requise"><Input value={value.required_integration ?? ""} onChange={(e) => set({ required_integration: e.target.value })} /></Field>
        <div className="grid gap-3 md:grid-cols-3">
          <Toggle label="Actif" checked={Boolean(value.is_active)} onChange={(is_active) => set({ is_active })} />
          <Toggle label="Visible utilisateur" checked={Boolean(value.is_user_visible)} onChange={(is_user_visible) => set({ is_user_visible })} />
          <Toggle label="Admin seulement" checked={Boolean(value.is_admin_only)} onChange={(is_admin_only) => set({ is_admin_only })} />
        </div>
        <Field label="Feature flags JSON"><Textarea className="font-mono text-xs" value={JSON.stringify(value.feature_flags ?? {}, null, 2)} onChange={(e) => safeJson(e.target.value, (feature_flags) => set({ feature_flags }))} /></Field>
        <Field label="Limites JSON"><Textarea className="font-mono text-xs" value={JSON.stringify(value.limits ?? {}, null, 2)} onChange={(e) => safeJson(e.target.value, (limits) => set({ limits }))} /></Field>
        <div className="flex gap-2">
          <Button onClick={() => onSave(value)} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
          <Button variant="outline" onClick={() => onChange(null)}>Annuler</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function safeJson(text: string, onValid: (value: Record<string, unknown>) => void) {
  try {
    onValid(JSON.parse(text));
  } catch {
    // Keep typing local until JSON is valid.
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>{label}</Label><Switch checked={checked} onCheckedChange={onChange} /></div>;
}
